import React from 'react';
import './styles.scss';
import Logo from '../../assets/icons/lusidpay_logo.svg';

const ShieldIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const CheckItem = ({ children }) => (
  <li className="ap-check-item">{children}</li>
);

const ApplyPage = () => {
  return (
    <div className="apply-page">

      {/* CUSTOM NAV */}
      <nav className="ap-nav">
        <div className="ap-nav-container">
          <span className="ap-nav-brand">LUSID <span className="ap-nav-brand-accent">CERTIFY</span></span>
          <span className="ap-nav-by">by</span>
          <img 
            src={Logo} 
            alt="LusidPay Logo" 
            className="ap-nav-logo" />
        </div>
      </nav>

      {/* HERO */}
      <section className="ap-hero">
        <div className="ap-container">
          <div className="ap-hero-content">
            <div className="ap-alert-badge">
              Action Required: DBE Recertification Mandates
            </div>

            <div className="ap-hero-subtitle">Introducing Lusid Certify</div>

            <h1 className="ap-h1">Protect Your DBE Certification Without Paying a Consultant $3,000.</h1>

            <p className="ap-hero-desc">
              The new USDOT Interim Final Rule requires a strictly formatted “Personal Narrative” to maintain your DBE status and bid on federally funded IIJA infrastructure contracts. Lusid Certify is an AI-guided platform that acts as “TurboTax for your DBE Narrative.” We securely map your real-world experience to the federal statute and generate an audit-ready PDF for your state agency in under an hour.
            </p>

            <div className="ap-federal-validation">
              <ShieldIcon />
              Our underlying compliance architecture has been reviewed by Federal Highway Administration (FHWA) Subject Matter Experts with zero technical questions or objections.
            </div>

            <a href="#pricing" className="ap-btn ap-btn-white ap-btn-lg">View Pricing &amp; Start Narrative</a>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="ap-section ap-bg-light">
        <div className="ap-container">
          <div className="ap-section-header">
            <h2 className="ap-h2">Choose Your Compliance License</h2>
            <p>Transparent, flat-fee pricing. No hourly legal bills or hidden consulting retainers.</p>
          </div>

          <div className="ap-pricing-grid">

            {/* Standard */}
            <div className="ap-pricing-card">
              <h3 className="ap-h3">Standard Narrative</h3>
              <p className="ap-card-sub">Best for a single business entity.</p>
              <div className="ap-price">$499</div>
              <div className="ap-price-effective">
                Effective Price: $399* <br />
                <span className="ap-price-effective-note">(After Activation Rebate)</span>
              </div>
              <ul className="ap-feature-list">
                <CheckItem>AI-Guided Plain English Interview</CheckItem>
                <CheckItem>Includes 1 Business Entity</CheckItem>
                <CheckItem>Includes 1 Disadvantaged Owner</CheckItem>
                <CheckItem>Mathematically Mapped CFR 49 Defense</CheckItem>
                <CheckItem>Instant Audit-Ready PDF Generation</CheckItem>
              </ul>
              <div className="ap-addon-note">+$199 per additional owner/entity</div>
              <button className="ap-btn ap-btn-outline ap-btn-full">Get Started</button>
            </div>

            {/* Multi-Entity - Popular */}
            <div className="ap-pricing-card ap-pricing-card--popular">
              <div className="ap-popular-badge">Most Popular</div>
              <h3 className="ap-h3">Multi-Entity Narrative</h3>
              <p className="ap-card-sub">Best for owners with multiple certified firms.</p>
              <div className="ap-price">$699</div>
              <div className="ap-price-effective">
                Effective Price: $599* <br />
                <span className="ap-price-effective-note">(After Activation Rebate)</span>
              </div>
              <ul className="ap-feature-list">
                <CheckItem>Everything in Standard, plus:</CheckItem>
                <CheckItem><strong>Includes up to 2 Business Entities</strong></CheckItem>
                <CheckItem>Includes 1 Disadvantaged Owner</CheckItem>
                <CheckItem>Shared Personal History Mapping</CheckItem>
                <CheckItem>Multi-Entity Financial Cross-Checking</CheckItem>
              </ul>
              <div className="ap-addon-note">+$199 per additional owner/entity</div>
              <button className="ap-btn ap-btn-primary ap-btn-full">Get Started</button>
            </div>

            {/* Prime Assist */}
            <div className="ap-pricing-card">
              <h3 className="ap-h3">Prime Assist</h3>
              <p className="ap-card-sub">Best for Prime Contractors securing their subs.</p>
              <div className="ap-price ap-price--custom">Custom Volume</div>
              <ul className="ap-feature-list ap-feature-list--mt">
                <CheckItem>Bulk License Purchasing</CheckItem>
                <CheckItem>White-Glove Concierge Onboarding</CheckItem>
                <CheckItem>Sub-tier Compliance Tracking</CheckItem>
                <CheckItem>Dedicated Account Manager</CheckItem>
              </ul>
              <button className="ap-btn ap-btn-outline ap-btn-full ap-btn-mt-auto">Talk to Sales</button>
            </div>

          </div>

          {/* REBATE BANNER */}
          <div className="ap-rebate-banner">
            <div>
              <h3 className="ap-rebate-h3">* The LusidPay LTV Rebate</h3>
              <p className="ap-rebate-p">
                Waiting 60+ days for Primes to pay invoices is the biggest hurdle for DBEs. We want to solve your cash flow, not just your compliance.
                <br /><br />
                Your Lusid Certify license includes full access to the <strong>LusidPay AP/AR platform</strong>. Connect your accounting software and run your vendor payments through us for 3 months, and we will instantly issue a <strong>$100 statement credit</strong> back to your linked bank account. Plus, active users permanently lock in a <strong>50% discount on Streamlined Annual Renewals</strong>—allowing our AI to securely update your financial cross-checks every year without starting from scratch.
              </p>
            </div>
            <div className="ap-rebate-logo-wrap">
              <img src={Logo} alt="LusidPay" className="ap-rebate-logo" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ap-section">
        <div className="ap-container">
          <div className="ap-section-header">
            <h2 className="ap-h2">Frequently Asked Questions</h2>
          </div>

          <div className="ap-faq-grid">
            <div className="ap-faq-box">
              <h4 className="ap-faq-h4">Can’t I just write this myself or use free AI like ChatGPT?</h4>
              <p>You can, but state analysts do not accept generic or emotional essays. A successful submission requires strict legal mapping to 49 CFR Part 26 and hyperlinked financial evidence (tax returns, loan denials). Generic AI tools cannot compile secure, audit-ready Personal Narrative Packages. Furthermore, inputting your highly sensitive corporate financial data into public AI models poses a massive security risk.</p>
            </div>

            <div className="ap-faq-box">
              <h4 className="ap-faq-h4">Is my financial data secure?</h4>
              <p>Absolutely. Lusid Certify provides a closed, encrypted, SOC-2 aligned vault. We process your data exclusively to generate your compliant PDF dossier, and your financial information is never used to train public language models.</p>
            </div>

            <div className="ap-faq-box">
              <h4 className="ap-faq-h4">Why is there an extra fee for multiple owners?</h4>
              <p>Under federal guidelines, every disadvantaged owner whose shares contribute to the required 51% majority must submit their own individual narrative and personal financial history. Processing an additional owner requires our system to run a completely separate compliance interview and defense compilation.</p>
            </div>

            <div className="ap-faq-box">
              <h4 className="ap-faq-h4">Will this work for my specific State DOT or UCP?</h4>
              <p>Yes. The requirement for a subjective Personal Narrative stems from the federal USDOT Interim Final Rule (49 CFR Part 26). Because our architecture maps directly to the federal statute, Because our interview and compilation architecture maps directly to the federal 49 CFR Part 26 statute, the resulting Personal Narrative is structured for submission to any certifying state agency nationwide.</p>
            </div>

            <div className="ap-faq-box">
              <h4 className="ap-faq-h4">How long does the process take?</h4>
              <p>Most business owners complete the AI-guided interview and document upload in 45 to 60 minutes. Your fully formatted, mathematically mapped PDF Personal Narrative Package is compiled instantly upon completion, ready to be submitted to your state portal.</p>
            </div>

            <div className="ap-faq-box">
              <h4 className="ap-faq-h4">What happens next year when I need to recertify?</h4>
              <p>Compliance is not a one-time event; the state requires an annual update of your financials and recent business history. When you process your narrative through Lusid Certify, your baseline data is vaulted. Next year, our system will perform a streamlined “Delta Update” to cross-check your latest tax returns and append any new barriers you faced. If you remain an active user of our included <strong>LusidPay AP/AR</strong> platform, you automatically unlock these <strong>Streamlined Annual Renewals at 50% off</strong> for the lifetime of your account.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="ap-footer">
        <div className="ap-footer-inner">
          <div className="ap-footer-brand-row">
            <span className="ap-footer-brand">LUSID <span className="ap-footer-brand-accent">CERTIFY</span></span>
            <span className="ap-footer-by">by</span>
            <img src={Logo} alt="LusidPay" className="ap-footer-logo" />
          </div>
          <p className="ap-footer-copy">&copy; 2026 LusidPay Inc. All Rights Reserved.</p>
          <p className="ap-footer-tagline">Built for secure, audit-ready federal compliance.</p>
        </div>
      </footer>

    </div>
  );
};

export default ApplyPage;